/** @jsxImportSource @emotion/react */

import React from "react";
import Immutable from "immutable";
import {css} from "@emotion/react";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import NovoLoading from "js/dashboards/novo-loading";

import MetricCurve from "js/dashboards/components/metric-curve";
import Ratio from "js/dashboards/components/ratios/ratio";
import Trend from "js/dashboards/components/trends/trend";
import UiGrid from "js/dashboards/components/grids/ui-grid";
import RatioComparison from "js/dashboards/components/ratio-comparison/ratio-comparison";
import AsyncServerGrid from "js/dashboards/components/grids/async-server-grid";
import PerformanceSnapshot from "js/dashboards/components/performance-snapshot/performance-snapshot";
import {BasicComponentTitleBar} from "js/dashboards/components/title-bar";

const PANEL_COMPONENTS = Immutable.Map({
  MetricCurve,
  Ratio,
  Trend,
  RatioComparison,
  AsyncServerGrid,
  UiGrid,
  PerformanceSnapshot
});

const panelContainerCss = theme => css`
  background-color: ${theme.palette.background.card};
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.05);
  font-size: 14px;
  color: ${theme.palette.text.main};
  border-radius: 5px;
  transition: background-color 0.2s;
  margin: 10px 0;
  width: max-content;
  position: relative;
`;

const panelContentCss = css`
  height: 80px;
  min-height: 80px;
  width: calc(100% + 20px);
  padding-top: 27px;
`;

const errorMessageCss = theme => css`
  color: ${theme.palette.error.main};
  font-style: italic;
`;

const loadingMessageCss = theme => css`
  color: ${theme.palette.text.secondary};
  font-style: italic;
  padding: 10px;
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const panelButtonCss = theme => css`
  border: 1px solid ${theme.palette.primary.main};
  background: ${theme.palette.background.card};
  color: ${theme.palette.primary.main};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
  margin-top: 0.2rem;
  display: inline-flex;
  align-items: center;

  &:first-of-type {
    margin-right: 0.5rem;
  }

  & i {
    margin-top: -3px; // offset to get bhi icons to line up nicely within buttons
    margin-left: 0.5rem;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AiAssistantPreview = React.memo(({
  componentConfig,
  onParentConfigChange,
  dashboardConfig,
  queueDataLoad,
  clearDataLoad,
  onDashboardChange,
  dashboard,
  componentTypeToTemplate
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const [panelData, setPanelData] = React.useState(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [transformedConfig, setTransformedConfig] = React.useState(null);
  const [mockComponent, setMockComponent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isAdding, setIsAdding] = React.useState(false);

  // Create a unique component ID for this preview
  const componentId = React.useMemo(() => `preview-${Date.now()}-${Math.random()}`, []);
  const componentLoadId = React.useMemo(() => Math.random(), []);

  const panelType = componentConfig.get("type");
  const panelTypeSupported = PANEL_COMPONENTS.has(panelType);

  // Clean up data loads when component unmounts
  React.useEffect(() => {
    return () => {
      if (clearDataLoad) {
        clearDataLoad(item => item.componentId === componentId && item.componentLoadId === componentLoadId);
      }
    };
  }, [componentId, componentLoadId, clearDataLoad]);

  React.useEffect(() => {
    // Early return if panel is not supported
    if (!panelTypeSupported) {
      setLoading(false);
      setError(`Unknown panel type: ${panelType}`);
      return;
    }

    const loadPanelData = async () => {
      try {
        setLoading(true);
        setError(null);

        const panelConfig = PANEL_COMPONENTS.get(panelType);
        const transformConfig = panelConfig.get("transformConfig");
        const loadData = panelConfig.get("load");
        const transformData = panelConfig.get("transformData");

        // Create the mock component once and store it in state
        const transformedConfig = transformConfig(componentConfig, dashboardConfig);
        setTransformedConfig(transformedConfig);

        const mockComponent = Immutable.fromJS({
          id: componentId,
          title: componentConfig.get("title"),
          dataConfig: transformedConfig
        });
        setMockComponent(mockComponent);

        const loadedData = await loadData(mockComponent);
        const transformedData = await transformData(loadedData);
        setPanelData(transformedData);
      } catch (err) {
        console.error("Error loading panel preview data:", err);
        setError("Failed to load panel data");
      } finally {
        setLoading(false);
      }
    };

    void loadPanelData();
  }, [componentConfig, panelType, panelTypeSupported, dashboardConfig, componentId]);

  const handleAddToDashboard = React.useCallback(async () => {
    if (!onDashboardChange || !dashboard || !componentTypeToTemplate || !transformedConfig) {
      return;
    }

    setIsAdding(true);

    try {
      const template = componentTypeToTemplate.get(panelType);
      const components = dashboard.getIn(["json", "components"], Immutable.List());
      const maxId = components.map(c => c.get("id")).max() || 0;
      const newComponentId = maxId + 1;
      const templateDimensions = template.getIn(["layout", "init"]);

      const bottomOfLastComponent = components
          .map(c => c.getIn(["layout", "y"]) + c.getIn(["layout", "height"]))
          .max() || 0;

      let newComponent = Immutable.Map({
        id: newComponentId,
        type: panelType,
        title: componentConfig.get("title") || template.get("getTitle")(mockComponent),
        dataConfig: transformedConfig,
        layout: templateDimensions
            .set("x", 0)
            .set("y", Math.ceil(bottomOfLastComponent))
      });

      const applyParent = template.get("applyParentConfig");
      if (applyParent) {
        const updatedDataConfig = applyParent(
            dashboard.getIn(["json", "dataConfig"]),
            newComponent.get("dataConfig")
        );
        newComponent = newComponent.set("dataConfig", updatedDataConfig);
      }

      onDashboardChange(dashboard.updateIn(
          ["json", "components"],
          Immutable.List(),
          components => components.push(newComponent)
      ), {
        reloadComponentId: newComponentId,
        scrollToComponent: true
      });

    } catch (err) {
      console.error("Error adding component to dashboard:", err);
      setError("Failed to add component to dashboard");
    } finally {
      setIsAdding(false);
    }
  }, [
    onDashboardChange,
    dashboard,
    componentTypeToTemplate,
    transformedConfig,
    panelType,
    componentConfig,
    mockComponent
  ]);

  const renderPanelContent = () => {
    // Early return if panel is not supported
    if (!panelTypeSupported) {
      return (
          <div css={panelContentCss}>
            <div css={errorMessageCss(theme)}>Unknown panel type: {panelType ?? "null"}</div>
          </div>
      );
    }

    const panelConfig = PANEL_COMPONENTS.get(panelType);
    const panelDimensions = panelConfig.get("previewDimensions").toJS();

    const expandedDimensions = panelConfig.get("expandedPreviewDimensions").toJS();

    if (loading) {
      return (
          <div css={panelContentCss} style={panelDimensions}>
            <div css={loadingMessageCss(theme)}>
              <NovoLoading />
            </div>
          </div>
      );
    } else if (error) {
      return (
          <div css={panelContentCss}>
            <div css={errorMessageCss(theme)}>{error}</div>
          </div>
      );
    } else if (!panelData) {
      return (
          <div css={panelContentCss}>
            <div css={errorMessageCss(theme)}>No data available</div>
          </div>
      );
    }

    const getReactComponent = panelConfig.get("getReactComponent");
    const PanelComponent = getReactComponent();
    const getTitle = panelConfig.get("getTitle");

    return (
        <div>
          <BasicComponentTitleBar
              config={mockComponent.get("dataConfig")}
              inheritKeys={mockComponent.getIn(["dataConfig", "inheritKeys"])}
              componentId={componentId}
              onRemoveClick={() => {}}
              onEditClick={() => {}}
              canDownload={false}
              isClickThrough={false}
              canFullScreen={false}
              isFullScreen={false}
              onFullScreenClick={() => {}}
              onDuplicateClick={() => {}}
              isEditing={false}
              titleIsEditable={false}
              title={getTitle(mockComponent)}
              variant={panelConfig.get("variant")}
              onTitleChange={() => {}} />
          <div css={panelContentCss} style={isExpanded ? expandedDimensions : panelDimensions}>
            <PanelComponent
                enableCharts={true}
                data={panelData}
                dimensions={isExpanded ? expandedDimensions : panelDimensions}
                config={transformedConfig}
                onRequestDialog={() => {}}
                onParentConfigChange={onParentConfigChange}
                componentLoadId={componentLoadId}
                queueDataLoad={queueDataLoad}
                clearDataLoad={clearDataLoad}
            />
          </div>
        </div>
    );
  };

  return (
      <>
        <div css={panelContainerCss(theme)}>
          {renderPanelContent()}
        </div>
        <button
            css={panelButtonCss(theme)}
            onClick={handleAddToDashboard}
            disabled={isAdding || loading || !!error || !onDashboardChange}
            data-test-id={"add-to-dashboard-button"}
        >
          {isAdding ? "Adding..." : "Add to dashboard"}
          <i className="bhi-add-thin" />
        </button>
        <button
            css={panelButtonCss(theme)}
            onClick={() => setIsExpanded(!isExpanded)}
            disabled={loading || !!error || !onDashboardChange}
            data-test-id={"expand-preview-button"}
        >
          {isExpanded ? "Collapse Preview" : "Expand Preview"}
          <i className={`bhi-${isExpanded ? "arrow-collapse" : "card-expand"}`} />
        </button>
      </>
  );
});

export default AiAssistantPreview;